import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Podcast, Newspaper, X } from 'lucide-react';
import { Button } from '../components/Button';
export function NewsMedia() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const mediaItems = [{
    type: 'news',
    icon: <Newspaper size={32} data-id="element-277" />,
    title: 'Lutheran Church Commissions Underground Piped Water System in Kakumiro',
    description: 'UG Reports covers the commissioning of an underground piped water system in Kakumiro, Uganda — a project supported by Big Love Foundation and local church partners.',
    date: 'March 2026',
    link: 'https://ugreports.co.ug/lutheran-church-commissions-underground-piped-water-system-in-kakumiro/',
    image: 'https://cdn.magicpatterns.com/uploads/n7AeuyvqEN2nQgHQBBjJF7/Screenshot_2026-03-04_at_8.57.29_PM.png'
  }, {
    type: 'podcast',
    icon: <Podcast size={32} data-id="element-278" />,
    title: 'Interview: The Heart Behind Big Love',
    description: 'Founder shares the vision, challenges, and victories of building a faith-based nonprofit.',
    date: 'November 2023',
    videoUrl: 'https://www.youtube.com/embed/mLVrzWa4e9g',
    image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800'
  }];
  return <div className="pt-20" data-id="element-279">
      {/* Hero */}
      <section className="bg-[var(--color-text)] text-white py-20" data-id="element-280">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-281">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-282">
            <h1 className="text-white mb-6" data-id="element-283">Big Love in the World</h1>
            <p className="text-xl text-white/90" data-id="element-284">
              Stories, media, and news from the Big Love community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Video */}
      <section className="py-20 bg-white" data-id="element-285">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-287">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-288">Featured Video</h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-3xl mx-auto" data-id="element-289">
              See Big Love Foundation's story and impact firsthand.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="aspect-video rounded-lg overflow-hidden shadow-2xl" data-id="element-290">
            <iframe width="100%" height="100%" src="https://www.youtube.com/embed/NQZm4gNuqTc" title="Big Love Foundation News Feature" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-291"></iframe>
          </motion.div>
        </div>
      </section>

      {/* Media Grid */}
      <section className="py-20 bg-[var(--color-gray-light)]" data-id="element-292">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-293">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-294">
            {mediaItems.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="bg-white rounded-lg overflow-hidden shadow-lg" data-id="element-295">
                <div className="h-48 bg-gray-200 overflow-hidden relative" data-id="element-296">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-297" />
                  <div className="absolute top-4 left-4 bg-[var(--color-primary)] text-white p-2 rounded-lg" data-id="element-298">
                    {item.icon}
                  </div>
                </div>
                <div className="p-6" data-id="element-299">
                  <div className="text-sm font-semibold text-[var(--color-primary)] mb-2 uppercase tracking-wide" data-id="element-300">
                    {item.type}
                  </div>
                  <h3 className="text-xl text-[var(--color-text)] mb-2 font-bold" data-id="element-301">
                    {item.title}
                  </h3>
                  <p className="text-[var(--color-text-light)] mb-4 leading-relaxed" data-id="element-302">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-303">
                    <span className="text-sm text-gray-500" data-id="element-304">{item.date}</span>
                    {item.videoUrl ? <Button variant="outline" size="sm" onClick={() => setActiveVideo(item.videoUrl)} data-id="element-305">
                        Watch Video
                      </Button> : <Button variant="outline" size="sm" href={item.link} data-id="element-306">
                        Read Article
                      </Button>}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white" data-id="element-307">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-308">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-309">
            <h2 className="text-[var(--color-text)] mb-6" data-id="element-310">
              Want to Share Our Story?
            </h2>
            <p className="text-xl text-[var(--color-text-light)] mb-8" data-id="element-311">
              We'd love to speak at your church, event, or organization about
              the work God is doing through Big Love Foundation.
            </p>
            <Button variant="primary" size="lg" href="/get-involved" data-id="element-312">
              Invite Us To Speak
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence data-id="element-313">
        {activeVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveVideo(null)} data-id="element-314">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-315">
              <button onClick={() => setActiveVideo(null)} className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/80 text-white rounded-full transition-colors" data-id="element-316">
                <X size={24} data-id="element-317" />
              </button>
              <iframe width="100%" height="100%" src={`${activeVideo}?autoplay=1`} title="Video Player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-318"></iframe>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}